import { State } from "@/shared/state/visualization";

import { HttpClient, robotoHeaders } from "../../http";

import {
  UpdateWorkspaceAccessibilityRequest,
  WorkspaceAccessibilityResponse,
} from "./WorkspaceAccessibility";
import { WorkspaceRecord } from "./WorkspaceRecord";

export class WorkspaceService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async createWorkspace(
    vizState: State,
    orgId: string,
  ): Promise<WorkspaceRecord> {
    const requestUrl = this.#httpClient.constructUrl("v1/viz/workspaces");

    const body = JSON.stringify({
      config: vizState,
    });

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId }),
      body,
    });

    return response.json<WorkspaceRecord>();
  }

  public async getWorkspace(
    orgId: string,
    workspaceId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<WorkspaceRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/viz/workspaces/id/${workspaceId}`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal: options?.abortSignal,
    });

    return response.json<WorkspaceRecord>();
  }

  public async updateWorkspace(
    workspaceId: string,
    vizState: State,
    orgId: string,
  ): Promise<WorkspaceRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/viz/workspaces/id/${workspaceId}`,
    );

    const body = JSON.stringify({
      config: vizState,
    });

    const response = await this.#httpClient.put(requestUrl, {
      headers: robotoHeaders({ orgId }),
      body,
    });

    return response.json<WorkspaceRecord>();
  }

  /**
   * Get the accessibility status of a workspace and its datasets.
   */
  public async getWorkspaceAccessibility(
    workspaceId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<WorkspaceAccessibilityResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/viz/workspaces/id/${workspaceId}/accessibility`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<WorkspaceAccessibilityResponse>();
  }

  /**
   * Update the accessibility of a workspace and all its datasets.
   */
  public async updateWorkspaceAccessibility(
    workspaceId: string,
    request: UpdateWorkspaceAccessibilityRequest,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<WorkspaceAccessibilityResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/viz/workspaces/id/${workspaceId}/accessibility`,
    );
    const body = JSON.stringify(request);
    const response = await this.#httpClient.put(requestUrl, {
      body,
      signal: options?.abortSignal,
    });
    return response.json<WorkspaceAccessibilityResponse>();
  }
}
